/**
 * Страница управления отделами и должностями
 * Drag & Drop интерфейс для реорганизации структуры
 */

import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import {
  useSortable
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  Building2,
  Users,
  Plus,
  MoreVertical,
  Edit,
  Trash2,
  GripVertical,
  Save,
  X,
  AlertCircle,
  RefreshCw,
  Download
} from 'lucide-react'
import { dataService } from '../../services/dataService'
import { logAction, ActionTypes } from '../utils/auditLogger'
import SearchInput from '../../components/common/SearchInput'
import { filterDepartmentsBySearchWithPositions, getSearchResultsCountWithPositions } from '../../utils/searchUtils'

// Компонент сортируемого отдела
const SortableDepartment = ({ department, positions, onEditDepartment, onDeleteDepartment, onEditPosition, onDeletePosition, onAddPosition, index }) => {
  const sortableId = department.id || `dept-${index}`
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sortableId })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const [isExpanded, setIsExpanded] = useState(true)
  const [showMenu, setShowMenu] = useState(false)

  const departmentPositions = positions.filter(pos => pos.department_id === department.id)
    .sort((a, b) => a.level - b.level || a.sort_order - b.sort_order)

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white rounded-lg border shadow-sm ${isDragging ? 'opacity-50' : ''}`}
    >
      {/* Заголовок отдела */}
      <div className="p-4 border-b flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button
            {...attributes}
            {...listeners}
            className="text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing"
          >
            <GripVertical className="w-5 h-5" />
          </button>
          
          <div 
            className="w-4 h-4 rounded"
            style={{ backgroundColor: department.color }}
          />
          
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">{department.name}</h3>
            <p className="text-sm text-gray-500">
              {departmentPositions.length} должност{departmentPositions.length === 1 ? 'ь' : 
               departmentPositions.length < 5 ? 'и' : 'ей'}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-gray-400 hover:text-gray-600 p-1"
          >
            <motion.div
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <Building2 className="w-4 h-4" />
            </motion.div>
          </button>

          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="text-gray-400 hover:text-gray-600 p-1"
            >
              <MoreVertical className="w-4 h-4" />
            </button>

            <AnimatePresence>
              {showMenu && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="absolute right-0 top-8 w-48 bg-white rounded-lg shadow-lg border z-10"
                >
                  <div className="py-1">
                    <button
                      onClick={() => {
                        onAddPosition(department.id)
                        setShowMenu(false)
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Добавить должность</span>
                    </button>
                    <button
                      onClick={() => {
                        onEditDepartment(department)
                        setShowMenu(false)
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                    >
                      <Edit className="w-4 h-4" />
                      <span>Редактировать</span>
                    </button>
                    <button
                      onClick={() => {
                        onDeleteDepartment(department.id)
                        setShowMenu(false)
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>Удалить</span>
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Список должностей */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="p-4 space-y-2">
              {departmentPositions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>Нет должностей в этом отделе</p>
                  <button
                    onClick={() => onAddPosition(department.id)}
                    className="mt-2 text-blue-600 hover:text-blue-700 text-sm"
                  >
                    Добавить первую должность
                  </button>
                </div>
              ) : (
                departmentPositions.map((position, index) => (
                  <motion.div
                    key={position.id || `position-${department.id}-${index}`}
                    layout
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">{position.title}</h4>
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span>Уровень {position.level}</span>
                        {position.description && (
                          <span className="truncate max-w-xs">{position.description}</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => onEditPosition(position)}
                        className="text-gray-400 hover:text-gray-600 p-1"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onDeletePosition(position.id)}
                        className="text-gray-400 hover:text-red-600 p-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// Модальное окно для редактирования отдела
const DepartmentModal = ({ department, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#3B82F6'
  })

  useEffect(() => {
    if (department) {
      setFormData({
        name: department.name || '',
        description: department.description || '',
        color: department.color || '#3B82F6'
      })
    } else {
      setFormData({
        name: '',
        description: '',
        color: '#3B82F6'
      })
    }
  }, [department, isOpen])

  const handleSubmit = (e) => {
    e.preventDefault()
    onSave(formData)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-lg w-full max-w-md mx-4"
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">
              {department ? 'Редактировать отдел' : 'Создать отдел'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Название отдела
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Введите название отдела"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Описание
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Описание отдела (необязательно)"
                rows={3}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Цвет
              </label>
              <div className="flex items-center space-x-3">
                <input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer"
                />
                <input
                  type="text"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="#3B82F6"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Отмена
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
              >
                <Save className="w-4 h-4" />
                <span>Сохранить</span>
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  )
}

// Модальное окно для редактирования должности
const PositionModal = ({ position, departments, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    department_id: '',
    level: 1,
    description: '',
    requirements_detailed: '',
    responsibilities: '',
    sort_order: 0
  })

  useEffect(() => {
    if (position) {
      setFormData({
        title: position.title || '',
        department_id: position.department_id || '',
        level: position.level || 1,
        description: position.description || '',
        requirements_detailed: position.requirements_detailed || '',
        responsibilities: position.responsibilities || '',
        sort_order: position.sort_order || 0
      })
    } else {
      setFormData({
        title: '',
        department_id: departments[0]?.id || '',
        level: 1,
        description: '',
        requirements_detailed: '',
        responsibilities: '',
        sort_order: 0
      })
    }
  }, [position, departments, isOpen])

  const handleSubmit = (e) => {
    e.preventDefault()
    onSave(formData)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-lg w-full max-w-lg mx-4"
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">
              {position ? 'Редактировать должность' : 'Создать должность'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Название должности
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Введите название должности"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Отдел
                </label>
                <select
                  value={formData.department_id}
                  onChange={(e) => setFormData({ ...formData, department_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  {departments.map((dept, index) => (
                    <option key={dept.id || `dept-option-${index}`} value={dept.id}>{dept.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Уровень
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={formData.level}
                  onChange={(e) => setFormData({ ...formData, level: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Описание
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Описание должности"
                rows={3}
              />
            </div>



            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Детальные требования к кандидату
              </label>
              <textarea
                value={formData.requirements_detailed}
                onChange={(e) => setFormData({ ...formData, requirements_detailed: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Подробные требования: образование, опыт, навыки..."
                rows={4}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ключевые обязанности
              </label>
              <textarea
                value={formData.responsibilities}
                onChange={(e) => setFormData({ ...formData, responsibilities: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Основные обязанности и задачи..."
                rows={4}
              />
            </div>



            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Отмена
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
              >
                <Save className="w-4 h-4" />
                <span>Сохранить</span>
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  )
}

// Модальное окно для отображения связанных переходов
const TransitionsConflictModal = ({ isOpen, onClose, positionTitle, transitions }) => {
  if (!isOpen) return null
  
  // Валидация: проверяем что transitions это массив
  const validTransitions = Array.isArray(transitions) ? transitions : []
  
  // Защита от пустого массива
  if (validTransitions.length === 0) {
    return null
  }
  
  // Обработчик клавиши Escape
  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      return () => document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose])

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-lg w-full max-w-2xl mx-4 max-h-[80vh] overflow-hidden flex flex-col"
      >
        <div className="p-6 border-b">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-900">
              Невозможно удалить должность
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-sm text-gray-600">
            Должность "<span className="font-medium">{positionTitle}</span>" связана с {validTransitions.length} карьерным{validTransitions.length === 1 ? ' переходом' : validTransitions.length < 5 ? 'и переходами' : 'и переходами'}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-3">
            {validTransitions.map((transition) => {
              const typeLabels = {
                'horizontal': '→ Горизонтальный',
                'vertical': '↑ Вертикальный',
                'diagonal': '↗ Диагональный'
              }
              
              return (
                <div
                  key={transition.id}
                  className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-xs font-medium text-gray-500 bg-gray-200 px-2 py-1 rounded">
                          {typeLabels[transition.transition_type] || transition.transition_type}
                        </span>
                      </div>
                      <div className="space-y-1">
                        <div className="text-sm">
                          <span className="text-gray-500">Из:</span>{' '}
                          <span className="font-medium text-gray-900">
                            {transition.from_position_title}
                          </span>
                          {transition.from_department_name && (
                            <span className="text-gray-400 text-xs ml-2">
                              ({transition.from_department_name})
                            </span>
                          )}
                        </div>
                        <div className="text-sm">
                          <span className="text-gray-500">В:</span>{' '}
                          <span className="font-medium text-gray-900">
                            {transition.to_position_title}
                          </span>
                          {transition.to_department_name && (
                            <span className="text-gray-400 text-xs ml-2">
                              ({transition.to_department_name})
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        <div className="p-6 border-t bg-gray-50">
          <p className="text-sm text-gray-600 mb-4">
            Для удаления этой должности сначала удалите связанные переходы на странице "Управление переходами"
          </p>
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Понятно
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

// Основной компонент страницы
const DepartmentsPage = () => {
  const [departments, setDepartments] = useState([])
  const [positions, setPositions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activeId, setActiveId] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  
  // Модальные окна
  const [departmentModal, setDepartmentModal] = useState({ isOpen: false, department: null })
  const [positionModal, setPositionModal] = useState({ isOpen: false, position: null, departmentId: null })
  const [transitionsConflictModal, setTransitionsConflictModal] = useState({ 
    isOpen: false, 
    positionTitle: '', 
    transitions: [] 
  })

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Загрузка данных
  const loadData = async (isMounted = () => true) => {
    try {
      setLoading(true)
      
      // Используем новый централизованный метод ожидания
      await dataService.waitForInitialization()
      
      if (!isMounted()) return // Проверка что компонент не размонтирован

      // Читаем данные из серверного API через dataService
      const depts = await dataService.getDepartments()
      const pos = await dataService.getPositions()
      
      if (!isMounted()) return // Финальная проверка перед setState
      
      setDepartments(depts)
      setPositions(pos)
      setError(null)
    } catch (err) {
      console.error('Ошибка загрузки данных:', err)
      
      if (!isMounted()) return
      
      // Более детальная обработка ошибок
      if (err.message.includes('таймаут')) {
        setError('Таймаут загрузки данных. Проверьте соединение.')
      } else if (err.message.includes('404')) {
        setError('Данные не найдены на сервере')
      } else {
        setError('Ошибка загрузки данных: ' + err.message)
      }
    } finally {
      if (isMounted()) {
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    let isMounted = true
    
    const loadDataSafe = () => loadData(() => isMounted)
    loadDataSafe()
    
    // Cleanup function
    return () => {
      isMounted = false
    }
  }, [])

  // Обработчики Drag & Drop
  const handleDragStart = (event) => {
    setActiveId(event.active.id)
  }

  const handleDragEnd = async (event) => {
    const { active, over } = event

    if (active.id !== over?.id) {
      const oldIndex = departments.findIndex((dept, index) => 
        (dept.id || `dept-${index}`) === active.id
      )
      const newIndex = departments.findIndex((dept, index) => 
        (dept.id || `dept-${index}`) === over.id
      )
      
      const newDepartments = arrayMove(departments, oldIndex, newIndex)
      setDepartments(newDepartments)

      // Обновляем sort_order в базе
      for (const [index, dept] of newDepartments.entries()) {
        await dataService.updateDepartment(dept.id, { ...dept, sort_order: index })
      }

      logAction(ActionTypes.BULK_UPDATE, { 
        table: 'departments', 
        count: newDepartments.length,
        action: 'reorder'
      })
    }

    setActiveId(null)
  }

  // Обработчики отделов
  const handleCreateDepartment = () => {
    setDepartmentModal({ isOpen: true, department: null })
  }

  const handleEditDepartment = (department) => {
    setDepartmentModal({ isOpen: true, department })
  }

  const handleSaveDepartment = async (formData) => {
    try {
      if (departmentModal.department) {
        // Обновление
        const result = await dataService.updateDepartment(departmentModal.department.id, formData)
        if (result.success) {
          await loadData(() => true) // Ручное обновление - всегда выполняем
          setDepartmentModal({ isOpen: false, department: null })
        } else {
          setError(result.error)
        }
      } else {
        // Создание
        const result = await dataService.createDepartment({
          ...formData,
          sort_order: departments.length
        })
        if (result.success) {
          await loadData(() => true) // Ручное обновление - всегда выполняем
          setDepartmentModal({ isOpen: false, department: null })
        } else {
          setError(result.error)
        }
      }
    } catch (err) {
      console.error('Ошибка сохранения отдела:', err)
      setError('Ошибка сохранения отдела')
    }
  }

  const handleDeleteDepartment = async (departmentId) => {
    const departmentPositions = positions.filter(pos => pos.department_id === departmentId)
    const positionsCount = departmentPositions.length
    
    let confirmMessage = 'Вы уверены, что хотите удалить этот отдел?'
    if (positionsCount > 0) {
      confirmMessage += `\n\nВнимание, все должности в данном отделе будут удалены (${positionsCount} должност${positionsCount === 1 ? 'ь' : positionsCount < 5 ? 'и' : 'ей'}).`
    }
    
    if (window.confirm(confirmMessage)) {
      try {
        // Удаляем отдел (сервер автоматически удалит все связанные должности и переходы)
        const result = await dataService.deleteDepartment(departmentId)
        if (result.success) {
          await loadData(() => true) // Ручное обновление - всегда выполняем
          
          // Показываем сообщение об успешном удалении
          if (result.deleted_positions_count > 0) {
            console.log(`✅ Отдел удален. Также удалено должностей: ${result.deleted_positions_count}`)
          }
        } else {
          setError(result.error)
        }
      } catch (err) {
        console.error('Ошибка удаления отдела:', err)
        setError('Ошибка удаления отдела')
      }
    }
  }

  // Обработчики должностей
  const handleAddPosition = (departmentId) => {
    setPositionModal({ isOpen: true, position: null, departmentId })
  }

  const handleEditPosition = (position) => {
    setPositionModal({ isOpen: true, position, departmentId: null })
  }

  const handleSavePosition = async (formData) => {
    try {
      if (positionModal.position) {
        // Обновление
        const result = await dataService.updatePosition(positionModal.position.id, formData)
        if (result.success) {
          await loadData(() => true) // Ручное обновление - всегда выполняем
          setPositionModal({ isOpen: false, position: null, departmentId: null })
        } else {
          setError(result.error)
        }
      } else {
        // Создание
        const result = await dataService.createPosition({
          ...formData,
          department_id: formData.department_id || positionModal.departmentId
        })
        if (result.success) {
          await loadData(() => true) // Ручное обновление - всегда выполняем
          setPositionModal({ isOpen: false, position: null, departmentId: null })
        } else {
          setError(result.error)
        }
      }
    } catch (err) {
      console.error('Ошибка сохранения должности:', err)
      setError('Ошибка сохранения должности')
    }
  }

  const handleDeletePosition = async (positionId) => {
    // Защита от множественных кликов
    if (loading) return
    
    if (window.confirm('Вы уверены, что хотите удалить эту должность?')) {
      try {
        setLoading(true)
        const result = await dataService.deletePosition(positionId)
        if (result.success) {
          await loadData(() => true) // Ручное обновление - всегда выполняем
          // Сбрасываем состояние модального окна конфликтов
          setTransitionsConflictModal({ isOpen: false, positionTitle: '', transitions: [] })
        } else {
          // Проверяем, есть ли информация о связанных переходах
          if (result.transitions && Array.isArray(result.transitions) && result.transitions.length > 0) {
            setTransitionsConflictModal({
              isOpen: true,
              positionTitle: result.position_title || 'Неизвестная должность',
              transitions: result.transitions
            })
          } else {
            setError(result.error)
          }
        }
      } catch (err) {
        console.error('Ошибка удаления должности:', err)
        
        // Пытаемся извлечь информацию о переходах из ошибки
        if (err.transitions && Array.isArray(err.transitions) && err.transitions.length > 0) {
          setTransitionsConflictModal({
            isOpen: true,
            positionTitle: err.position_title || 'Неизвестная должность',
            transitions: err.transitions
          })
        } else {
          setError('Ошибка удаления должности')
        }
      } finally {
        setLoading(false)
      }
    }
  }



  // Фильтрация отделов по поисковому запросу
  const filteredDepartments = filterDepartmentsBySearchWithPositions(departments, positions, searchTerm)
  const searchResults = getSearchResultsCountWithPositions(departments, positions, searchTerm)

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Загрузка данных...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Заголовок и действия */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Управление отделами</h1>
          <p className="text-gray-600">Drag & Drop редактор организационной структуры</p>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={handleCreateDepartment}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Добавить отдел</span>
          </button>
        </div>
      </div>

      {/* Ошибки */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-2"
        >
          <AlertCircle className="w-5 h-5 text-red-600" />
          <span className="text-red-700">{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-600 hover:text-red-700"
          >
            <X className="w-4 h-4" />
          </button>
        </motion.div>
      )}

      {/* Поиск */}
      <div className="bg-white p-4 rounded-lg border">
        <div className="flex items-center justify-between">
          <SearchInput
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Поиск по отделам и названиям должностей..."
            className="flex-1 max-w-md"
          />
          {searchTerm && (
            <div className="text-sm text-gray-500">
              Найдено: {searchResults.departments} отдел{searchResults.departments === 1 ? '' : searchResults.departments < 5 ? 'а' : 'ов'}, {searchResults.positions} должност{searchResults.positions === 1 ? 'ь' : searchResults.positions < 5 ? 'и' : 'ей'}
            </div>
          )}
        </div>
      </div>

      {/* Статистика */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center space-x-3">
            <Building2 className="w-8 h-8 text-blue-600" />
            <div>
              <p className="text-sm text-gray-500">Отделов{searchTerm ? ' (найдено)' : ''}</p>
              <p className="text-2xl font-bold text-gray-900">{searchTerm ? searchResults.departments : departments.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center space-x-3">
            <Users className="w-8 h-8 text-green-600" />
            <div>
              <p className="text-sm text-gray-500">Должностей{searchTerm ? ' (найдено)' : ''}</p>
              <p className="text-2xl font-bold text-gray-900">{searchTerm ? searchResults.positions : positions.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center space-x-3">
            <GripVertical className="w-8 h-8 text-purple-600" />
            <div>
              <p className="text-sm text-gray-500">Уровней</p>
              <p className="text-2xl font-bold text-gray-900">
                {Math.max(...positions.map(p => p.level), 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Drag & Drop список отделов */}
      <div className="space-y-4">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={filteredDepartments.map((d, index) => d.id || `dept-${index}`)} strategy={verticalListSortingStrategy}>
            {filteredDepartments.map((department, index) => (
              <SortableDepartment
                key={department.id || `department-${index}`}
                department={department}
                positions={positions}
                index={index}
                onEditDepartment={handleEditDepartment}
                onDeleteDepartment={handleDeleteDepartment}
                onEditPosition={handleEditPosition}
                onDeletePosition={handleDeletePosition}
                onAddPosition={handleAddPosition}
              />
            ))}
          </SortableContext>

          <DragOverlay>
            {activeId ? (
              <div className="bg-white rounded-lg border shadow-lg opacity-90">
                <div className="p-4 border-b">
                  <div className="flex items-center space-x-3">
                    <GripVertical className="w-5 h-5 text-gray-400" />
                    <div className="w-4 h-4 rounded bg-blue-500" />
                    <span className="font-semibold">
                      {departments.find((d, index) => (d.id || `dept-${index}`) === activeId)?.name}
                    </span>
                  </div>
                </div>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>

        {filteredDepartments.length === 0 && departments.length > 0 && searchTerm && (
          <div className="text-center py-12 bg-white rounded-lg border">
            <Building2 className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Ничего не найдено</h3>
            <p className="text-gray-500 mb-4">По запросу "{searchTerm}" ничего не найдено</p>
            <button
              onClick={() => setSearchTerm('')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Очистить поиск
            </button>
          </div>
        )}

        {departments.length === 0 && (
          <div className="text-center py-12 bg-white rounded-lg border">
            <Building2 className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Нет отделов</h3>
            <p className="text-gray-500 mb-4">Создайте первый отдел для начала работы с организационной структурой</p>
            <button
              onClick={handleCreateDepartment}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 mx-auto"
            >
              <Plus className="w-4 h-4" />
              <span>Создать первый отдел</span>
            </button>
          </div>
        )}
      </div>

      {/* Модальные окна */}
      <AnimatePresence>
        {departmentModal.isOpen && (
          <DepartmentModal
            key="department-modal"
            department={departmentModal.department}
            isOpen={departmentModal.isOpen}
            onClose={() => setDepartmentModal({ isOpen: false, department: null })}
            onSave={handleSaveDepartment}
          />
        )}
      </AnimatePresence>
      
      <AnimatePresence>
        {positionModal.isOpen && (
          <PositionModal
            key="position-modal"
            position={positionModal.position}
            departments={departments}
            isOpen={positionModal.isOpen}
            onClose={() => setPositionModal({ isOpen: false, position: null, departmentId: null })}
            onSave={handleSavePosition}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {transitionsConflictModal.isOpen && (
          <TransitionsConflictModal
            key="transitions-conflict-modal"
            isOpen={transitionsConflictModal.isOpen}
            positionTitle={transitionsConflictModal.positionTitle}
            transitions={transitionsConflictModal.transitions}
            onClose={() => setTransitionsConflictModal({ isOpen: false, positionTitle: '', transitions: [] })}
          />
        )}
      </AnimatePresence>
    </div>
  )
}

export default DepartmentsPage 